import { easeOutQuad, isSsr, useResponsiveValue } from '@lemonade-hq/cdk';
import { clampedInversedLerp } from '@lemonade-hq/ts-helpers';
import React from 'react';
import type { ResponsiveTween } from './tweens';

type Easing = (t: number) => number;
type ResponsiveNumberPair = [number, number];
export type ResponsiveValue =
  | [ResponsiveNumberPair]
  | [ResponsiveNumberPair, ResponsiveNumberPair]
  | [ResponsiveNumberPair, ResponsiveNumberPair, ResponsiveNumberPair];

interface UseResponsiveTweenParams {
  t: number;
  tt: ResponsiveValue;
  tween?: ResponsiveTween<React.CSSProperties>;
  ease?: Easing;
  allowSSR?: boolean;
}

type TweenStyle = { t: number; style: React.CSSProperties };

export function useResponsiveTween({
  t,
  tt,
  tween,
  ease = easeOutQuad,
  allowSSR = false,
}: UseResponsiveTweenParams): TweenStyle {
  const rightTT = useResponsiveValue(tt);
  const relevantT = clampedInversedLerp(rightTT[0], rightTT[1], t);
  const rightTween = useResponsiveValue(tween);
  const style = rightTween != null ? rightTween(ease(relevantT)) : {};
  if (!allowSSR && isSsr()) return { style: {}, t: relevantT };

  return { style, t: relevantT };
}

type TweenerPropsRenderChildren = (params: { t: number; style: React.CSSProperties }) => React.ReactElement;
interface TweenerBaseProps extends UseResponsiveTweenParams {
  style?: React.CSSProperties;
}

interface TweenerProps extends TweenerBaseProps {
  render?: TweenerPropsRenderChildren;
  children?:
    | React.ReactNode
    | React.ReactNode[]
    | ((props: { t: number; style: React.CSSProperties }) => React.ReactNode | React.ReactNode[]);
}

const Tweener: React.FC<TweenerProps> = ({
  t,
  tt,
  tween = undefined,
  ease = easeOutQuad,
  allowSSR = false,
  children,
  ...props
}: TweenerProps) => {
  const { style, t: relevantT } = useResponsiveTween({
    t,
    tt,
    tween,
    ease,
    allowSSR,
  });

  if ('render' in props) {
    return props.render?.({ t: relevantT, style }) ?? null;
  }

  if (typeof children === 'function') {
    return children({ t: relevantT, style }) as React.ReactNode;
  }

  return React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;

    return React.cloneElement<TweenStyle>(child as React.ReactElement<TweenStyle>, {
      t: relevantT,
      style: { ...child.props.style, ...props.style, ...style }, // eslint-disable-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      ...props,
    });
  }) as React.ReactNode;
};
export default Tweener;
